import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { api } from '../lib/api'
import { birthDateYmdFromResidentDigits, digitsOnly, todayYmdLocal } from '../lib/claimContactFormat'
import ClaimReceiptTypePick from '../components/ClaimReceiptTypePick'
import ClaimAdditionalFieldInputs from '../components/claim/ClaimAdditionalFieldInputs'
import {
  isAdditionalFieldVisible,
  normalizeAdditionalFieldDefinitions,
  validateRequiredAdditionalFields,
} from '../lib/claimAdditionalFields'
import {
  CLAIM_MAX_DOCUMENT_PAGE_COUNT,
  claimDocumentPageCount,
  claimDocumentsPageCount,
  prepareClaimDocumentFiles,
  takeClaimDocumentsWithinPageLimit,
} from '../lib/claimDocumentPages'

function clampYmdToToday(ymd) {
  const s = String(ymd || '').trim()
  if (!s) return s
  const max = todayYmdLocal()
  return s > max ? max : s
}

const RESERVED_FIELD_KEYS = new Set([
  'insuredName', 'insuredResidentNo', 'insuredPhone', 'insuredAddress',
  'contractorName', 'contractorResidentNo', 'contractorPhone', 'contractorAddress',
  'beneficiaryName', 'beneficiaryResidentNo', 'beneficiaryPhone', 'beneficiaryAddress',
  'compensationContactName', 'compensationContactPhone', 'compensationContactType',
  'legalHeirRepresentativeName', 'legalHeirRepresentativeRelation', 'legalHeirFatherName', 'legalHeirMotherName',
  'bankName', 'bankAutoDebitRequested', 'bankAccountNumber', 'accountRelationToInsured',
  'accidentType', 'accidentReceiptType', 'accidentDescription',
  'requiredConsentAgreed', 'optionalConsentAgreed',
  'printedAtYear', 'printedAtMonth', 'printedAtDay',
])
const CLAIM_MAX_DOCUMENT_COUNT = CLAIM_MAX_DOCUMENT_PAGE_COUNT

export default function ClaimIntakePublicPage() {
  const { token } = useParams()
  const today = new Date()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [documents, setDocuments] = useState([])
  const [templateFieldKeys, setTemplateFieldKeys] = useState([])
  const [additionalFieldDefs, setAdditionalFieldDefs] = useState([])
  const [extraFields, setExtraFields] = useState({})

  const [customerBirthDate, setCustomerBirthDate] = useState('')
  const [customerPhone, setCustomerPhone] = useState('')
  const [claimSection, setClaimSection] = useState('')
  const [claimItem, setClaimItem] = useState('')
  const [claimAmount, setClaimAmount] = useState('')
  const [claimDate, setClaimDate] = useState('')
  const [accidentDate, setAccidentDate] = useState('')
  const [memo, setMemo] = useState('')
  const [signatureDataUrl, setSignatureDataUrl] = useState('')
  const [website, setWebsite] = useState('')
  const [insuredName, setInsuredName] = useState('')
  const [insuredResidentNoFront, setInsuredResidentNoFront] = useState('')
  const [insuredResidentNoBack, setInsuredResidentNoBack] = useState('')
  const [insuredPhone, setInsuredPhone] = useState('')
  const [insuredAddress, setInsuredAddress] = useState('')
  const [contractorName, setContractorName] = useState('')
  const [contractorResidentNoFront, setContractorResidentNoFront] = useState('')
  const [contractorResidentNoBack, setContractorResidentNoBack] = useState('')
  const [contractorPhone, setContractorPhone] = useState('')
  const [contractorAddress, setContractorAddress] = useState('')
  const [isContractorBeneficiarySame, setIsContractorBeneficiarySame] = useState(true)
  const [beneficiaryName, setBeneficiaryName] = useState('')
  const [beneficiaryResidentNoFront, setBeneficiaryResidentNoFront] = useState('')
  const [beneficiaryResidentNoBack, setBeneficiaryResidentNoBack] = useState('')
  const [beneficiaryPhone, setBeneficiaryPhone] = useState('')
  const [beneficiaryAddress, setBeneficiaryAddress] = useState('')
  const [compensationContactName, setCompensationContactName] = useState('')
  const [compensationContactPhone, setCompensationContactPhone] = useState('')
  const [compensationContactType, setCompensationContactType] = useState('CONTRACTOR')
  const [legalHeirRepresentativeName, setLegalHeirRepresentativeName] = useState('')
  const [legalHeirRepresentativeRelation, setLegalHeirRepresentativeRelation] = useState('')
  const [legalHeirFatherName, setLegalHeirFatherName] = useState('')
  const [legalHeirMotherName, setLegalHeirMotherName] = useState('')
  const [bankName, setBankName] = useState('')
  const [bankAutoDebitRequested, setBankAutoDebitRequested] = useState(false)
  const [bankAccountNumber, setBankAccountNumber] = useState('')
  const [accountRelationToInsured, setAccountRelationToInsured] = useState('')
  const [accidentType, setAccidentType] = useState('DISEASE')
  const [accidentReceiptType, setAccidentReceiptType] = useState('NEW')
  const [accidentDescription, setAccidentDescription] = useState('')
  const [requiredConsentAgreed, setRequiredConsentAgreed] = useState(false)
  const [optionalConsentAgreed, setOptionalConsentAgreed] = useState(false)
  const [printedAtYear, setPrintedAtYear] = useState(String(today.getFullYear()))
  const [printedAtMonth, setPrintedAtMonth] = useState(String(today.getMonth() + 1).padStart(2, '0'))
  const [printedAtDay, setPrintedAtDay] = useState(String(today.getDate()).padStart(2, '0'))
  const canvasRef = React.useRef(null)
  const drawingRef = React.useRef(false)

  const applyPlannerPrefill = React.useCallback((p, templateKeys) => {
    if (!p || typeof p !== 'object') return
    if (p.customerBirthDate) setCustomerBirthDate(String(p.customerBirthDate))
    if (p.claimDate) setClaimDate(clampYmdToToday(p.claimDate))
    if (p.accidentDate) setAccidentDate(clampYmdToToday(p.accidentDate))
    if (p.claimSection) setClaimSection(String(p.claimSection))
    if (p.claimItem) setClaimItem(String(p.claimItem))
    if (p.claimAmount != null && p.claimAmount !== '') setClaimAmount(String(p.claimAmount))
    if (p.memo) setMemo(String(p.memo))
    if (p.insuredName) setInsuredName(String(p.insuredName))
    if (p.contractorName) setContractorName(String(p.contractorName))
    if (p.insuredPhone) {
      setInsuredPhone(String(p.insuredPhone))
      setCustomerPhone(String(p.insuredPhone))
    }
    const splitRn = (raw) => {
      const d = String(raw || '').replace(/\D/g, '')
      if (d.length >= 13) return [d.slice(0, 6), d.slice(6, 13)]
      if (d.length > 6) return [d.slice(0, 6), d.slice(6)]
      return [d, '']
    }
    if (p.insuredResidentNo) {
      const [a, b] = splitRn(p.insuredResidentNo)
      setInsuredResidentNoFront(a)
      setInsuredResidentNoBack(b)
      if (!p.customerBirthDate) {
        const bd = birthDateYmdFromResidentDigits(digitsOnly(p.insuredResidentNo, 13))
        if (bd) setCustomerBirthDate(bd)
      }
    }
    if (p.contractorResidentNo) {
      const [a, b] = splitRn(p.contractorResidentNo)
      setContractorResidentNoFront(a)
      setContractorResidentNoBack(b)
    }
    const xf = p.extraFields
    if (xf && typeof xf === 'object') {
      if (xf.customerAddress) setInsuredAddress(String(xf.customerAddress))
      if (xf.beneficiaryName && !p.contractorName) setBeneficiaryName(String(xf.beneficiaryName))
      if (xf.accidentReceiptType === 'NEW' || xf.accidentReceiptType === 'ADDITIONAL') {
        setAccidentReceiptType(String(xf.accidentReceiptType))
      }
    }
  }, [])

  useEffect(() => {
    let ignore = false
    ;(async () => {
      try {
        const res = await api.get(`/claims/intake/public/${token}`)
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        if (!ignore) {
          setCustomerPhone(payload?.customerPhone || '')
          setInsuredName(payload?.customerName || '')
          setInsuredPhone(payload?.customerPhone || '')
          const keys = Array.isArray(payload?.templateFieldKeys) ? payload.templateFieldKeys : []
          const additionalDefs = normalizeAdditionalFieldDefinitions(payload?.additionalFields)
          setTemplateFieldKeys(keys)
          setAdditionalFieldDefs(additionalDefs)
          const init = {}
          keys.forEach((k) => { init[k] = '' })
          additionalDefs.forEach((field) => { init[field.fieldKey] = '' })
          const pref = payload?.plannerPrefill
          if (pref?.extraFields && typeof pref.extraFields === 'object') {
            for (const k of [...keys, ...additionalDefs.map((field) => field.fieldKey)]) {
              const v = pref.extraFields[k]
              if (v != null && v !== '') init[k] = String(v)
            }
          }
          setExtraFields(init)
          applyPlannerPrefill(pref, keys)
        }
      } catch (e) {
        if (!ignore) setError('유효하지 않거나 만료된 링크입니다.')
      } finally {
        if (!ignore) setLoading(false)
      }
    })()
    return () => { ignore = true }
  }, [token, applyPlannerPrefill])

  const onSubmit = async (e) => {
    e.preventDefault()
    if (submitted || submitting) return
    if (!insuredName.trim()) {
      setError('피보험자 이름을 입력해 주세요.')
      return
    }
    if (!contractorName.trim()) {
      setError('계약자 이름을 입력해 주세요.')
      return
    }
    if (!requiredConsentAgreed) {
      setError('필수 동의는 반드시 체크해야 합니다.')
      return
    }
    if (!signatureDataUrl) {
      setError('서명을 입력해 주세요.')
      return
    }
    const additionalFieldError = validateRequiredAdditionalFields(additionalFieldDefs, extraFields)
    if (additionalFieldError) {
      setError(additionalFieldError)
      return
    }
    const finalCustomerName = insuredName.trim()
    const finalCustomerPhone = insuredPhone || customerPhone || ''
    const beneficiaryResidentNo = isContractorBeneficiarySame
      ? `${contractorResidentNoFront}-${contractorResidentNoBack}`
      : `${beneficiaryResidentNoFront}-${beneficiaryResidentNoBack}`
    const visibleExtraFields = { ...extraFields }
    additionalFieldDefs.forEach((field) => {
      if (!isAdditionalFieldVisible(field, additionalFieldDefs, extraFields)) {
        delete visibleExtraFields[field.fieldKey]
      }
    })
    const mergedExtraFields = {
      ...visibleExtraFields,
      insuredName: insuredName.trim(),
      insuredResidentNo: `${insuredResidentNoFront}-${insuredResidentNoBack}`,
      insuredPhone: insuredPhone || '',
      insuredAddress: insuredAddress || '',
      contractorName: contractorName.trim(),
      contractorResidentNo: `${contractorResidentNoFront}-${contractorResidentNoBack}`,
      contractorPhone: contractorPhone || '',
      contractorAddress: contractorAddress || '',
      beneficiaryName: isContractorBeneficiarySame ? contractorName.trim() : (beneficiaryName || ''),
      beneficiaryResidentNo,
      beneficiaryPhone: isContractorBeneficiarySame ? (contractorPhone || '') : (beneficiaryPhone || ''),
      beneficiaryAddress: isContractorBeneficiarySame ? (contractorAddress || '') : (beneficiaryAddress || ''),
      compensationContactName: compensationContactName || '',
      compensationContactPhone: compensationContactPhone || '',
      compensationContactType,
      legalHeirRepresentativeName: legalHeirRepresentativeName || '',
      legalHeirRepresentativeRelation: legalHeirRepresentativeRelation || '',
      legalHeirFatherName: legalHeirFatherName || '',
      legalHeirMotherName: legalHeirMotherName || '',
      bankName: bankName || '',
      bankAutoDebitRequested: String(bankAutoDebitRequested),
      bankAccountNumber: bankAccountNumber || '',
      accountRelationToInsured: accountRelationToInsured || '',
      accidentType,
      accidentReceiptType,
      accidentDescription: accidentDescription || '',
      requiredConsentAgreed: String(requiredConsentAgreed),
      optionalConsentAgreed: String(optionalConsentAgreed),
      printedAtYear,
      printedAtMonth,
      printedAtDay,
    }
    setError('')
    setSubmitting(true)
    try {
      await api.post(`/claims/intake/public/${token}/submit`, {
        customerName: finalCustomerName,
        customerBirthDate: customerBirthDate || null,
        customerPhone: finalCustomerPhone || null,
        claimSection: claimSection || null,
        claimItem: claimItem || null,
        claimAmount: claimAmount ? Number(claimAmount) : null,
        claimDate: claimDate || null,
        accidentDate: accidentDate || null,
        memo: memo || null,
        signatureDataUrl: signatureDataUrl || null,
        website: website || '',
        extraFields: mergedExtraFields,
        documents: takeClaimDocumentsWithinPageLimit(documents, CLAIM_MAX_DOCUMENT_COUNT).map((d, idx) => ({
          fileId: d.fileId,
          documentType: 'CUSTOMER_ATTACH',
          label: d.originalName || `첨부서류_${idx + 1}`,
          sortOrder: idx,
        })),
      })
      setSubmitted(true)
    } catch (e) {
      setError(e?.response?.data?.message || '제출에 실패했습니다. 잠시 후 다시 시도해 주세요.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <div className="min-h-screen p-6 text-sm text-slate-600">불러오는 중...</div>
  const additionalFieldKeySet = new Set(additionalFieldDefs.map((field) => field.fieldKey))
  const customTemplateFieldKeys = templateFieldKeys.filter((k) => !RESERVED_FIELD_KEYS.has(k) && !additionalFieldKeySet.has(k))
  const documentPageCount = claimDocumentsPageCount(documents)

  const startDraw = (x, y) => {
    const c = canvasRef.current
    if (!c) return
    const ctx = c.getContext('2d')
    if (!ctx) return
    drawingRef.current = true
    ctx.beginPath()
    ctx.moveTo(x, y)
  }

  const draw = (x, y) => {
    const c = canvasRef.current
    if (!c || !drawingRef.current) return
    const ctx = c.getContext('2d')
    if (!ctx) return
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.strokeStyle = '#111827'
    ctx.lineTo(x, y)
    ctx.stroke()
  }

  const stopDraw = () => {
    drawingRef.current = false
    const c = canvasRef.current
    if (!c) return
    try {
      setSignatureDataUrl(c.toDataURL('image/png'))
    } catch {}
  }

  const getPos = (e) => {
    const c = canvasRef.current
    const rect = c.getBoundingClientRect()
    if (e.touches && e.touches[0]) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top }
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }

  const clearSignature = () => {
    const c = canvasRef.current
    if (!c) return
    const ctx = c.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, c.width, c.height)
    setSignatureDataUrl('')
  }

  const applyBirthFromInsuredResident = (frontRaw, backRaw) => {
    const f = digitsOnly(frontRaw, 6)
    const b = digitsOnly(backRaw, 7)
    const bd = birthDateYmdFromResidentDigits(`${f}${b}`)
    if (bd) setCustomerBirthDate(bd)
  }

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []).filter(Boolean)
    if (files.length === 0) return
    const currentPageCount = claimDocumentsPageCount(documents)
    const remaining = Math.max(0, CLAIM_MAX_DOCUMENT_COUNT - currentPageCount)
    if (remaining <= 0) {
      setError(`첨부서류는 최대 ${CLAIM_MAX_DOCUMENT_COUNT}장까지 가능합니다.`)
      e.target.value = ''
      return
    }
    let prepared
    try {
      prepared = await prepareClaimDocumentFiles(files, {
        initialPageCount: currentPageCount,
        maxPageCount: CLAIM_MAX_DOCUMENT_COUNT,
      })
    } catch {
      setError('PDF 페이지 수를 확인할 수 없습니다.')
      e.target.value = ''
      return
    }
    const acceptedFiles = prepared.accepted
    const skippedCount = prepared.skippedByPageLimit
    if (acceptedFiles.length === 0) {
      setError(`첨부서류는 최대 ${CLAIM_MAX_DOCUMENT_COUNT}장까지 가능합니다.`)
      e.target.value = ''
      return
    }
    setUploading(true)
    setError(skippedCount > 0 ? `최대 ${CLAIM_MAX_DOCUMENT_COUNT}장까지만 첨부되어 초과 ${skippedCount}개는 제외했습니다.` : '')
    const uploaded = []
    let failed = 0
    try {
      for (const item of acceptedFiles) {
        try {
          const { file, pageCount } = item
          const form = new FormData()
          form.append('file', file)
          const res = await api.post(`/claims/intake/public/${token}/upload`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
          const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
          if (payload?.fileId) {
            uploaded.push({
              ...payload,
              pageCount: Number(payload?.pageCount) > 0 ? Number(payload.pageCount) : pageCount,
            })
          }
        } catch {
          failed += 1
        }
      }
      if (uploaded.length > 0) {
        setDocuments(prev => takeClaimDocumentsWithinPageLimit([...prev, ...uploaded], CLAIM_MAX_DOCUMENT_COUNT))
      }
      if (failed > 0) {
        setError(
          failed === acceptedFiles.length
            ? '첨부 업로드에 실패했습니다.'
            : `${failed}개 파일 업로드에 실패했습니다. (${acceptedFiles.length - failed}개 성공)`,
        )
      }
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="mx-auto max-w-2xl rounded border bg-white p-6">
        <h1 className="text-xl font-bold">보험금 청구 정보 입력</h1>

        {submitted ? (
          <div className="mt-6 rounded border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
            제출이 완료되었습니다. 담당자가 확인 후 안내드리겠습니다.
          </div>
        ) : (
          <form className="mt-5 space-y-3" onSubmit={onSubmit}>
            <div className="rounded border p-3">
              <div className="text-sm font-medium text-slate-800">피보험자</div>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                <input className="rounded border px-3 py-2 text-sm" placeholder="이름 *" value={insuredName} onChange={(e) => setInsuredName(e.target.value)} />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    className="rounded border px-3 py-2 text-sm"
                    placeholder="주민번호 앞자리"
                    inputMode="numeric"
                    autoComplete="off"
                    value={insuredResidentNoFront}
                    onChange={(e) => {
                      const v = digitsOnly(e.target.value, 6)
                      setInsuredResidentNoFront(v)
                      applyBirthFromInsuredResident(v, insuredResidentNoBack)
                    }}
                  />
                  <input
                    className="rounded border px-3 py-2 text-sm"
                    placeholder="주민번호 뒷자리"
                    inputMode="numeric"
                    autoComplete="off"
                    value={insuredResidentNoBack}
                    onChange={(e) => {
                      const v = digitsOnly(e.target.value, 7)
                      setInsuredResidentNoBack(v)
                      applyBirthFromInsuredResident(insuredResidentNoFront, v)
                    }}
                  />
                </div>
                <input className="rounded border px-3 py-2 text-sm" placeholder="연락처" value={insuredPhone} onChange={(e) => setInsuredPhone(e.target.value)} />
                <input className="rounded border px-3 py-2 text-sm" placeholder="주소" value={insuredAddress} onChange={(e) => setInsuredAddress(e.target.value)} />
              </div>
            </div>

            <div className="rounded border p-3">
              <div className="text-sm font-medium text-slate-800">계약자</div>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                <input className="rounded border px-3 py-2 text-sm" placeholder="이름 *" value={contractorName} onChange={(e) => setContractorName(e.target.value)} />
                <div className="grid grid-cols-2 gap-2">
                  <input className="rounded border px-3 py-2 text-sm" placeholder="주민번호 앞자리" value={contractorResidentNoFront} onChange={(e) => setContractorResidentNoFront(e.target.value)} />
                  <input className="rounded border px-3 py-2 text-sm" placeholder="주민번호 뒷자리" value={contractorResidentNoBack} onChange={(e) => setContractorResidentNoBack(e.target.value)} />
                </div>
                <input className="rounded border px-3 py-2 text-sm" placeholder="연락처" value={contractorPhone} onChange={(e) => setContractorPhone(e.target.value)} />
                <input className="rounded border px-3 py-2 text-sm" placeholder="주소" value={contractorAddress} onChange={(e) => setContractorAddress(e.target.value)} />
              </div>
            </div>

            <div className="rounded border p-3">
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={isContractorBeneficiarySame} onChange={(e) => setIsContractorBeneficiarySame(e.target.checked)} />
                계약자와 수익자가 동일합니다.
              </label>
            </div>

            {!isContractorBeneficiarySame && (
              <div className="rounded border p-3">
                <div className="text-sm font-medium text-slate-800">수익자</div>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                  <input className="rounded border px-3 py-2 text-sm" placeholder="이름" value={beneficiaryName} onChange={(e) => setBeneficiaryName(e.target.value)} />
                  <div className="grid grid-cols-2 gap-2">
                    <input className="rounded border px-3 py-2 text-sm" placeholder="주민번호 앞자리" value={beneficiaryResidentNoFront} onChange={(e) => setBeneficiaryResidentNoFront(e.target.value)} />
                    <input className="rounded border px-3 py-2 text-sm" placeholder="주민번호 뒷자리" value={beneficiaryResidentNoBack} onChange={(e) => setBeneficiaryResidentNoBack(e.target.value)} />
                  </div>
                  <input className="rounded border px-3 py-2 text-sm" placeholder="연락처" value={beneficiaryPhone} onChange={(e) => setBeneficiaryPhone(e.target.value)} />
                  <input className="rounded border px-3 py-2 text-sm" placeholder="주소" value={beneficiaryAddress} onChange={(e) => setBeneficiaryAddress(e.target.value)} />
                </div>
              </div>
            )}

            <div className="rounded border p-3">
              <div className="text-sm font-medium text-slate-800">보상안내 받으실 분</div>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2">
                <input className="rounded border px-3 py-2 text-sm" placeholder="이름" value={compensationContactName} onChange={(e) => setCompensationContactName(e.target.value)} />
                <input className="rounded border px-3 py-2 text-sm" placeholder="연락처" value={compensationContactPhone} onChange={(e) => setCompensationContactPhone(e.target.value)} />
                <select className="rounded border px-3 py-2 text-sm" value={compensationContactType} onChange={(e) => setCompensationContactType(e.target.value)}>
                  <option value="CONTRACTOR">보험계약자</option>
                  <option value="INSURED">피보험자</option>
                </select>
              </div>
            </div>

            <div className="rounded border p-3">
              <div className="text-sm font-medium text-slate-800">미성년자 계약의 법정상속인</div>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                <input className="rounded border px-3 py-2 text-sm" placeholder="대표 1인 이름" value={legalHeirRepresentativeName} onChange={(e) => setLegalHeirRepresentativeName(e.target.value)} />
                <input className="rounded border px-3 py-2 text-sm" placeholder="대표 1인과의 관계" value={legalHeirRepresentativeRelation} onChange={(e) => setLegalHeirRepresentativeRelation(e.target.value)} />
                <input className="rounded border px-3 py-2 text-sm" placeholder="(생보) 부 이름" value={legalHeirFatherName} onChange={(e) => setLegalHeirFatherName(e.target.value)} />
                <input className="rounded border px-3 py-2 text-sm" placeholder="(생보) 모 이름" value={legalHeirMotherName} onChange={(e) => setLegalHeirMotherName(e.target.value)} />
              </div>
            </div>

            <div className="rounded border p-3">
              <div className="text-sm font-medium text-slate-800">보험금 수령계좌</div>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                <input className="rounded border px-3 py-2 text-sm" placeholder="금융기관명" value={bankName} onChange={(e) => setBankName(e.target.value)} />
                <input className="rounded border px-3 py-2 text-sm" placeholder="계좌번호" value={bankAccountNumber} onChange={(e) => setBankAccountNumber(e.target.value)} />
                <input className="rounded border px-3 py-2 text-sm" placeholder="피보험자와의 관계" value={accountRelationToInsured} onChange={(e) => setAccountRelationToInsured(e.target.value)} />
                <label className="inline-flex items-center gap-2 rounded border px-3 py-2 text-sm">
                  <input type="checkbox" checked={bankAutoDebitRequested} onChange={(e) => setBankAutoDebitRequested(e.target.checked)} />
                  자동이체 계좌요청
                </label>
              </div>
            </div>

            <div className="rounded border p-3">
              <div className="text-sm font-medium text-slate-800">사고사항 입력</div>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                <select className="rounded border px-3 py-2 text-sm" value={accidentType} onChange={(e) => setAccidentType(e.target.value)}>
                  <option value="DISEASE">질병</option>
                  <option value="INJURY">상해</option>
                </select>
              </div>
              <div className="mt-3">
                <div className="text-xs font-medium text-slate-600 mb-2">접수 구분</div>
                <ClaimReceiptTypePick
                  idPrefix="intake-receipt"
                  value={accidentReceiptType === 'ADDITIONAL' ? 'ADDITIONAL' : 'NEW'}
                  onChange={setAccidentReceiptType}
                />
              </div>
              <textarea className="mt-2 w-full rounded border px-3 py-2 text-sm" rows={3} placeholder="사고경위" value={accidentDescription} onChange={(e) => setAccidentDescription(e.target.value)} />
            </div>

            <div className="rounded border p-3">
              <div className="text-sm font-medium text-slate-800">청구 기본 정보</div>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                <input className="rounded border px-3 py-2 text-sm" placeholder="생년월일 (예: 1990-01-01)" value={customerBirthDate} onChange={(e) => setCustomerBirthDate(e.target.value)} />
                <input className="rounded border px-3 py-2 text-sm" placeholder="고객 연락처(보조)" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} />
                <input className="rounded border px-3 py-2 text-sm" placeholder="청구 구분" value={claimSection} onChange={(e) => setClaimSection(e.target.value)} />
                <input className="rounded border px-3 py-2 text-sm" placeholder="청구 항목" value={claimItem} onChange={(e) => setClaimItem(e.target.value)} />
                <input type="number" className="rounded border px-3 py-2 text-sm" placeholder="청구 금액" value={claimAmount} onChange={(e) => setClaimAmount(e.target.value)} />
                <input className="rounded border px-3 py-2 text-sm" placeholder="추가 메모" value={memo} onChange={(e) => setMemo(e.target.value)} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                type="date"
                className="w-full rounded border px-3 py-2 text-sm"
                max={todayYmdLocal()}
                value={claimDate}
                onChange={(e) => {
                  const v = e.target.value
                  const m = todayYmdLocal()
                  if (v && v > m) return
                  setClaimDate(v)
                }}
              />
              <input
                type="date"
                className="w-full rounded border px-3 py-2 text-sm"
                max={todayYmdLocal()}
                value={accidentDate}
                onChange={(e) => {
                  const v = e.target.value
                  const m = todayYmdLocal()
                  if (v && v > m) return
                  setAccidentDate(v)
                }}
              />
            </div>
            <div className="rounded border p-3">
              <div className="text-sm font-medium text-slate-800">청구서 작성일자</div>
              <div className="mt-2 grid grid-cols-3 gap-2">
                <input className="rounded border px-3 py-2 text-sm" placeholder="년" value={printedAtYear} onChange={(e) => setPrintedAtYear(e.target.value)} />
                <input className="rounded border px-3 py-2 text-sm" placeholder="월" value={printedAtMonth} onChange={(e) => setPrintedAtMonth(e.target.value)} />
                <input className="rounded border px-3 py-2 text-sm" placeholder="일" value={printedAtDay} onChange={(e) => setPrintedAtDay(e.target.value)} />
              </div>
            </div>

            <div className="rounded border p-3">
              <div className="text-sm font-medium text-slate-800">동의 항목</div>
              <div className="mt-2 space-y-2 text-sm">
                <label className="inline-flex items-center gap-2">
                  <input type="checkbox" checked={requiredConsentAgreed} onChange={(e) => setRequiredConsentAgreed(e.target.checked)} />
                  필수 동의 (필수)
                </label>
                <div className="flex items-center gap-4">
                  <span>선택 동의</span>
                  <label className="inline-flex items-center gap-1">
                    <input type="radio" name="optionalConsentAgreed" checked={optionalConsentAgreed} onChange={() => setOptionalConsentAgreed(true)} />
                    동의
                  </label>
                  <label className="inline-flex items-center gap-1">
                    <input type="radio" name="optionalConsentAgreed" checked={!optionalConsentAgreed} onChange={() => setOptionalConsentAgreed(false)} />
                    미동의
                  </label>
                </div>
              </div>
            </div>
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="absolute -left-[9999px] top-auto h-0 w-0 opacity-0"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />

            <ClaimAdditionalFieldInputs
              fields={additionalFieldDefs}
              values={extraFields}
              onChange={setExtraFields}
            />

            {customTemplateFieldKeys.length > 0 && (
              <div className="rounded border p-3">
                <div className="text-sm font-medium text-slate-800">추가 입력 항목</div>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                  {customTemplateFieldKeys.map((k) => (
                    <div key={k}>
                      <div className="text-xs text-slate-500">{k}</div>
                      <input
                        className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
                        value={extraFields[k] || ''}
                        onChange={(e) => setExtraFields((prev) => ({ ...prev, [k]: e.target.value }))}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded border p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-medium text-slate-800">첨부서류 업로드</div>
                <div className={`rounded border px-2 py-1 text-xs font-semibold ${
                  documentPageCount >= CLAIM_MAX_DOCUMENT_COUNT
                    ? 'border-amber-200 bg-amber-50 text-amber-800'
                    : 'border-slate-200 bg-slate-50 text-slate-600'
                }`}>
                  {documentPageCount} / {CLAIM_MAX_DOCUMENT_COUNT}장
                </div>
              </div>
              <div className="mt-2">
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  onChange={onUpload}
                  disabled={uploading || documentPageCount >= CLAIM_MAX_DOCUMENT_COUNT}
                />
              </div>
              {documents.length > 0 && (
                <div className="mt-2 space-y-1">
                  {documents.map((d, idx) => (
                    <div key={`${d.fileId}_${idx}`} className="flex items-center justify-between rounded bg-slate-50 px-2 py-1 text-xs">
                      <span>{d.originalName || d.fileId} · {claimDocumentPageCount(d)}장</span>
                      <button
                        type="button"
                        className="text-rose-600"
                        onClick={() => setDocuments(prev => prev.filter((_, i) => i !== idx))}
                      >
                        제거
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded border p-3">
              <div className="text-sm font-medium text-slate-800">서명</div>
              <div className="mt-2 rounded border bg-white p-2">
                <canvas
                  ref={canvasRef}
                  width={560}
                  height={180}
                  className="w-full border rounded touch-none"
                  onMouseDown={(e) => { const p = getPos(e); startDraw(p.x, p.y) }}
                  onMouseMove={(e) => { const p = getPos(e); draw(p.x, p.y) }}
                  onMouseUp={stopDraw}
                  onMouseLeave={stopDraw}
                  onTouchStart={(e) => { e.preventDefault(); const p = getPos(e); startDraw(p.x, p.y) }}
                  onTouchMove={(e) => { e.preventDefault(); const p = getPos(e); draw(p.x, p.y) }}
                  onTouchEnd={(e) => { e.preventDefault(); stopDraw() }}
                />
              </div>
              <div className="mt-2 flex justify-end">
                <button type="button" className="rounded border px-3 py-1.5 text-xs hover:bg-slate-50" onClick={clearSignature}>
                  서명 지우기
                </button>
              </div>
            </div>

            {error && <div className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</div>}

            <div className="flex justify-end">
              <button
                type="submit"
                className="rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={submitting}
              >
                {submitting ? '제출 중...' : '제출하기'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
